<template>
    <el-config-provider :locale="locales[$store.state.settings.defaultLang]">
        <Header v-if="showHeader"></Header>
        <RouterView :key="routeViewKey" :style="{
            '--g-main-sidebar-actual-width': mainSidebarActualWidth,
            '--g-sub-sidebar-actual-width': subSidebarActualWidth,
        }" />
    </el-config-provider>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Header from '@/components/Header/index.vue';
import { getElementLocales } from '@/locales';
import Cookies from 'js-cookie';

const { proxy } = getCurrentInstance();
const store = useStore();
const route = useRoute();

let cookieCheckInterval = null; // 全局定时器句柄（非响应式）

const isLogin = computed(() => store.getters['user/isLogin']);
const isProduction = process.env.NODE_ENV === 'production';
function checkAuthCookieAndLogout () {
    const hasCookie = Cookies.get('auth_token');
    if (!hasCookie && isLogin.value) {
        console.log('⚠️ 检测到 auth_token Cookie 丢失，执行自动登出');
        store.dispatch('user/logout').then(() => {
            if (route.name !== 'login') {
                proxy.$router.push({ name: 'login' }).catch(() => { });
            }
        });
    }
}

function startCookiePolling () {
    if (cookieCheckInterval) return; // 防止重复启动
    checkAuthCookieAndLogout(); // 立即检查一次
    cookieCheckInterval = setInterval(checkAuthCookieAndLogout, 1500); // 每1.5秒检查一次
}

function stopCookiePolling () {
    if (cookieCheckInterval) {
        clearInterval(cookieCheckInterval);
        cookieCheckInterval = null;
    }
}

// 监听登录状态，动态启停轮询
watch(
    () => isLogin.value,
    (newVal) => {
        if (newVal) {
            startCookiePolling();
        } else {
            stopCookiePolling();
        }
    },
    { immediate: true }
);

// 页面卸载时清理
onBeforeUnmount(() => {
    stopCookiePolling();
});
// 原有逻辑保持不变
const authPaths = ['/login', '/register'];
const authNames = ['login', 'register'];
const showHeader = computed(() => {
    if (route.meta?.hideHeader) {
        return false;
    }
    // 登录页不显示 Header（已有自己的登录表单）
    if (authPaths.includes(route.path) || authNames.includes(route.name)) {
        return false;
    }
    return true;
});

const data = ref({
    times: null,
});

provide('generateI18nTitle', generateI18nTitle);

const locales = computed(() => getElementLocales());
const routeViewKey = computed(() => {
    if (
        route.path.startsWith('/notice')
    ) {
        return route.path;
    }
    return route.fullPath;
});

// 侧边栏主导航当前实际宽度
const mainSidebarActualWidth = computed(() => {
    let actualWidth = getComputedStyle(document.documentElement).getPropertyValue('--g-main-sidebar-width');
    actualWidth = parseInt(actualWidth);
    if (['head', 'single', 'only-head'].includes(store.state.settings.menuMode)) {
        actualWidth = 0;
    }
    return `${actualWidth}px`;
});

// 侧边栏次导航当前实际宽度
const subSidebarActualWidth = computed(() => {
    let actualWidth = getComputedStyle(document.documentElement).getPropertyValue('--g-sub-sidebar-width');
    actualWidth = parseInt(actualWidth);
    if (store.state.settings.sidebarCollapse) {
        actualWidth = 64;
    }
    if (['only-side', 'only-head'].includes(store.state.settings.menuMode)) {
        actualWidth = 0;
    }
    if (
        store.state.settings.sidebarOnylOneHide &&
        store.getters['menu/sidebarRoutes'].length === 1 &&
        (!store.getters['menu/sidebarRoutes'][0].children ||
            store.getters['menu/sidebarRoutes'][0]?.children.every((item) => item.meta.sidebar === false))
    ) {
        actualWidth = 0;
    }
    return `${actualWidth}px`;
});

// 主题/模式监听（保持不变）
watch(
    () => store.state.settings.mode,
    () => {
        if (store.state.settings.mode === 'pc') {
            store.commit('settings/updateThemeSetting', {
                sidebarCollapse: store.state.settings.sidebarCollapseLastStatus,
            });
        } else if (store.state.settings.mode === 'mobile') {
            store.commit('settings/updateThemeSetting', {
                sidebarCollapse: true,
            });
        }
        document.body.setAttribute('data-mode', store.state.settings.mode);
    },
    { immediate: true }
);

watch(
    () => store.state.settings.theme,
    () => {
        document.body.setAttribute('data-theme', store.state.settings.theme);
    },
    { immediate: true }
);

watch(
    () => store.state.settings.menuMode,
    () => {
        document.body.setAttribute('data-menu-mode', store.state.settings.menuMode);
    },
    { immediate: true }
);

watch(
    () => store.state.settings.appWidthMode,
    () => {
        document.body.setAttribute('data-app-width-mode', store.state.settings.appWidthMode);
    },
    { immediate: true }
);

watch(
    [() => store.state.settings.enableDynamicTitle, () => store.state.settings.title],
    () => {
        if (store.state.settings.enableDynamicTitle && store.state.settings.title) {
            let title = generateI18nTitle(route.meta.i18n, store.state.settings.title);
            document.title = `${title} - ${import.meta.env.VITE_APP_TITLE}`;
        } else {
            document.title = import.meta.env.VITE_APP_TITLE;
        }
    },
    { immediate: true }
);

onMounted(() => {
    window.onresize = () => {
        store.commit('settings/setMode', document.documentElement.clientWidth);
    };
    window.onresize();
});

onBeforeUnmount(() => {
    clearInterval(data.value.times);
});

function generateI18nTitle (key, defaultTitle) {
    return !!key && proxy.$te(key) ? proxy.$t(key) : defaultTitle;
}
</script>

<style>
.single-ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.double-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

html,
body {
    height: auto !important;
}
</style>
